// Problems tab, the live diagnostics for the active contract. Each problem is a
// card: severity icon, [code], message, "source/args:line:col", a CLI-style code
// frame (the offending line with the error span underlined), and any sub-notes.
// Clicking a card reveals it in the editor. Source and args diagnostics are
// grouped; empty + all-clear states are handled.

import type { ReactNode } from "react";
import { CircleAlert, TriangleAlert, Info, Check } from "lucide-react";
import { useIde, argsPathFor } from "../store";
import type { Diagnostic, DiagFile, Severity } from "../types";
import { EmptyState, SectionHead, gotoEditor } from "./shared";

function SeverityIcon({ severity }: { severity: Severity }) {
  if (severity === "error") return <CircleAlert size={14} strokeWidth={2} />;
  if (severity === "warning") return <TriangleAlert size={14} strokeWidth={2} />;
  return <Info size={14} strokeWidth={2} />;
}

const FILE_LABEL: Record<DiagFile, string> = {
  source: "Source",
  args: "Arguments",
};

/** A CLI-style code frame: the offending source line(s) with the error span
 *  underlined in the severity color. Multi-line spans are capped to stay tidy. */
function CodeFrame({
  source,
  start,
  end,
  severity,
}: {
  source: string;
  start: { line: number; col: number };
  end: { line: number; col: number };
  severity: Severity;
}) {
  if (!source) return null;
  const lines = source.split("\n");
  if (start.line < 1 || start.line > lines.length) return null;
  const lastLine = Math.min(end.line, start.line + 3, lines.length);
  const rows: ReactNode[] = [];
  for (let ln = start.line; ln <= lastLine; ln++) {
    const text = lines[ln - 1] ?? "";
    const sc = ln === start.line ? Math.max(0, start.col - 1) : 0;
    const ec = Math.max(ln === end.line ? end.col - 1 : text.length, sc + 1);
    rows.push(
      <span className="pn-frame-row" key={ln}>
        <span className="pn-frame-ln">{ln}</span>
        <code className="pn-frame-code">
          {text.slice(0, sc)}
          <span className={`pn-frame-mark pn-frame-mark--${severity}`}>
            {text.slice(sc, ec) || " "}
          </span>
          {text.slice(ec)}
        </code>
      </span>,
    );
  }
  return <span className="pn-frame">{rows}</span>;
}

function ProblemRow({ diag, source }: { diag: Diagnostic; source: string }) {
  return (
    <button
      type="button"
      className="pn-prob"
      onClick={() => gotoEditor(diag.start.line, diag.start.col)}
      title={`Reveal at line ${diag.start.line}, column ${diag.start.col}`}
    >
      <span className="pn-prob-head">
        <span className={`pn-prob-ico pn-prob-ico--${diag.severity}`}>
          <SeverityIcon severity={diag.severity} />
        </span>
        <span className="pn-prob-msg">
          {diag.code && <span className="pn-prob-code">{diag.code}</span>}
          {diag.message}
        </span>
        <span className="pn-prob-loc">
          {diag.file === "source" ? "source" : "args"}:{diag.start.line}:{diag.start.col}
        </span>
      </span>
      {diag.label && <span className="pn-prob-label">{diag.label}</span>}
      <CodeFrame source={source} start={diag.start} end={diag.end} severity={diag.severity} />
      {diag.notes.length > 0 && (
        <span className="pn-prob-notes">
          {diag.notes.map((note, i) => (
            <span className="pn-prob-note" key={i}>
              <span className={`pn-prob-note-tag pn-prob-note-tag--${note.severity}`}>
                {note.severity}
              </span>
              <span>{note.message}</span>
            </span>
          ))}
        </span>
      )}
    </button>
  );
}

function Group({ file, diags, source }: { file: DiagFile; diags: Diagnostic[]; source: string }) {
  if (diags.length === 0) return null;
  return (
    <div className="pn-prob-group">
      <SectionHead
        title={FILE_LABEL[file]}
        count={`${diags.length} ${diags.length === 1 ? "problem" : "problems"}`}
      />
      <div className="pn-prob-list">
        {diags.map((d, i) => (
          <ProblemRow diag={d} source={source} key={`${d.code}-${d.span.start}-${i}`} />
        ))}
      </div>
    </div>
  );
}

export function ProblemsPanel() {
  const diagnostics = useIde((s) => s.diagnostics);
  const status = useIde((s) => s.status);
  const activePath = useIde((s) => s.activePath);
  const files = useIde((s) => s.files);

  const hasContract = activePath != null && files[activePath]?.language === "seal";

  if (!hasContract) {
    return (
      <div className="pn-scroll">
        <EmptyState
          icon={<Info size={22} strokeWidth={1.5} />}
          title="No contract open"
          hint="Open a .sl contract to see diagnostics."
        />
      </div>
    );
  }

  if (diagnostics.length === 0) {
    return (
      <div className="pn-scroll">
        <EmptyState
          tone="ok"
          icon={<Check size={22} strokeWidth={2.25} />}
          title="No problems"
          hint={
            status === "linting"
              ? "Checking…"
              : "This contract type-checks. Run Compile (⌘↵) to lower it to script."
          }
        />
      </div>
    );
  }

  const sourceContent = activePath ? (files[activePath]?.content ?? "") : "";
  const argsContent = activePath ? (files[argsPathFor(activePath)]?.content ?? "") : "";
  const source = diagnostics.filter((d) => d.file === "source");
  const args = diagnostics.filter((d) => d.file === "args");

  return (
    <div className="pn-scroll">
      <Group file="source" diags={source} source={sourceContent} />
      <Group file="args" diags={args} source={argsContent} />
    </div>
  );
}
